import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { I18n } from "@/i18n/I18n";
import { FileTypes } from "@/types/fileTypes";
import { useState, useEffect, useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { Document, Page } from "react-pdf";
import { File as FileModel } from "@/backend/models/file";
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface FilePreviewContentProps {
  file: FileModel;
  onScrolledToBottom?: () => void;
}

export function FilePreviewContent({ file, onScrolledToBottom = () => {} }: FilePreviewContentProps) {
  const getFileSourceUrl = useGlobalStore(state => state.getFileSourceUrl);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);

  useEffect(() => {
    void getFileSourceUrl(file.id).then(response => {
      if (response.status === 200) {
        setSourceUrl(response.downloadUrl);
      }
    });
  }, [file.id, getFileSourceUrl]);

  if (!sourceUrl) {
    return <Spinner animation="border" variant="light" role="status">
      <span className="visually-hidden">{I18n.get('loadingFile')}</span>
    </Spinner>;
  }

  if (file.type === FileTypes.IMAGE) {
    return (
      <div className="file-preview-content">
        <img src={sourceUrl} alt={file.fileName} />
      </div>
    );
  }

  if (file.type === FileTypes.VIDEO) {
    return (
      <div className="file-preview-content">
        <video src={sourceUrl} controls autoPlay muted aria-label={file.fileName} />
      </div>
    );
  }

  if (file.type === FileTypes.PDF) {
    return <PdfScrollableContent sourceUrl={sourceUrl} fileName={file.fileName} onScrolledToBottom={onScrolledToBottom} />;
  }

  return null;
}

function PdfScrollableContent({ sourceUrl, fileName, onScrolledToBottom }: { sourceUrl: string; fileName: string; onScrolledToBottom: () => void }) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 30) {
      onScrolledToBottom();
    }
  }, []);

  return (
    <div
      className="file-preview-content file-preview-content--pdf"
      ref={containerRef}
      onScroll={handleScroll}
      role="document"
      aria-label={fileName}
    >
      {/* react-pdf 11 suspends by default; keep its built-in loading/error states. */}
      <Document
        file={sourceUrl}
        suspense={false}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
      >
        {numPages && Array.from({ length: numPages }, (_, i) => (
          <Page
            key={i + 1}
            pageNumber={i + 1}
            width={600}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        ))}
      </Document>
    </div>
  );
}